import React, { ReactNode } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { Layout } from "../layout/Layout";
import { CurrencyCode } from "../price/Price";
import { PaymentMethod, ServicePeriodCode, ServiceProductCode } from "../../graphql/schema";

import styles from "./digital-product-base.module.scss";

export interface DigitalProductBaseProps {
  children?: ReactNode;
  isPaymentInfoVisible?: boolean;
  isProductActive?: boolean;
  title: string;
  detail?: string;
  publicUrl?: string;
  logo?: JSX.Element | null;
  subscribers?: JSX.Element;
  price?: string | JSX.Element;
  priceNext?: string | JSX.Element;
  exclusiveOffer?: JSX.Element | false;
  isLoading?: boolean;
  gracePeriod?: {
    dateEnd?: Date;
    expiresInMS?: number;
    subscription: {
      id: string;
      products: { code: ServiceProductCode }[];
      billingAmount?: string | null;
      billingCurrency: {
        code: CurrencyCode;
      };
      periodCode: ServicePeriodCode;
    };
  };
  producerName?: string;
  bottomMessage?: string;
  comingSoon?: boolean;
  isLight?: boolean;
  exclusiveOfferExpiryDate?: Date;
  exclusiveOfferTimeLeftMS?: number;
  onPaymentMethodChange?(paymentMethod: PaymentMethod): void;
  onExclusiveOfferClick?(): void;
  onGracePeriodRenewalSuccess?(): void;
  attention?: boolean;
  isFree?: boolean;
}

export const DigitalProductBase: React.FC<DigitalProductBaseProps> = ({
  children,
  isPaymentInfoVisible = true,
  isProductActive,
  title,
  detail,
  logo,
  price,
  priceNext,

  producerName,
  bottomMessage,

  isLight = false,
  attention = false,
  isFree = false,
}) => {
  const { t } = useTranslation();
  //   const { isMobile } = useWindowDimensionsContext();
  //   const [isEditSubscriptionOpen, setIsEditSubscriptionOpen] = useState(false);
  //   const [isRenewingSubscription, setIsRenewingSubscription] = useState(false);

  //   const offerTimeLeft = timeLeft(exclusiveOfferTimeLeftMS ?? 0, t);
  //   const offerExpiryDate = exclusiveOfferExpiryDate
  //     ? formatLocaleDate(exclusiveOfferExpiryDate, i18n.language, { month: "long" })
  //     : "";

  //   const gracePeriodTimeLeft = timeLeft(gracePeriod?.expiresInMS ?? 0, t);
  //   const gracePeriodExpiryDate = gracePeriod?.dateEnd
  //     ? formatLocaleDate(gracePeriod.dateEnd, i18n.language, { month: "long" })
  //     : "";

  return (
    <div
      className={classNames(styles.wrap, {
        [styles["wrap--attention"]]: attention,
      })}
    >
      <Layout className={styles.main} wrap="TABLET_PORTRAIT_MAX" contentLeft>
        <div className={styles["content-row"]}>
          {/* Left side: Logo */}
          <div className={styles.logo}>{logo}</div>

          {/* Right side: Title, detail, and children */}
          <div className={styles["right-side"]}>
            <div className={styles["header-top"]}>{title}</div>

            <div className={styles["title-content"]}>
              {isPaymentInfoVisible && detail && (
                <div
                  className={classNames(styles.status, {
                    [styles["status--active"]]: isProductActive,
                    [styles["status--inactive"]]: !isProductActive,
                  })}
                >
                  <div
                    className={classNames(styles["status-title"], {
                      [styles["status-title--attention"]]: attention,
                    })}
                  >
                    {detail}
                  </div>
                  {price && <div className={styles.price}>{price}</div>}
                </div>
              )}
              {priceNext && <div className={styles["price-disclaimer"]}>{priceNext}</div>}
              {isFree && <div className={styles["status--is-free"]}>{t("Free Access")}</div>}
            </div>

            {/* Children (buttons, etc.) */}
            <div className={styles["button-area"]}>{children}</div>
          </div>
        </div>

        {producerName && (
          <p
            className={classNames(styles.producer)}
            dangerouslySetInnerHTML={{ __html: `${t("Powered by")} ${producerName}` }}
          />
        )}
        {bottomMessage && <p className={classNames(styles.producer, { [styles.light]: isLight })}>{bottomMessage}</p>}
      </Layout>
    </div>
  );
};
